import Button from 'react-bootstrap/Button';
import { IoBuild, IoCloseCircleOutline } from 'react-icons/io5';
import { Tooltip } from 'react-tooltip';

import { useAuth } from '@contexts/User.context';
import { useProject } from '@contexts/Project.context';
import { useMapToolBusy } from '@hooks/useMapToolBusy';
import { getGridFeatures } from '@utils/api';
import { ANNOTATION_TARGET, PROJECT_MODE } from '@utils/constants';

import AnnotateOrthophoto from '../ai/AnnotateOrthophoto';
import AnnotateSingleImages from '../ai/AnnotateSingleImages';
import { useAnnotationBoundaryDraw } from './useAnnotationBoundaryDraw';
import { useTrainingPermission } from './useTrainingPermission';

const annotationSidebarId = 'annotationSidebar';

/**
 * Model view's way into annotation mode: Train AI (no detections yet) or
 * Improve AI Accuracy. The picked model gives the grid and the classes; the
 * boxes drawn train that model.
 */
export default function Annotate() {
	const {
		project,
		projectMode,
		detections,
		pickedTask,
		toolBarVisible,
		isDemo,
		mapObject,
		annotationMode,
		setActiveSidebar,
		removeFromActiveSidebars,
		selectedSingleImage,
		dispatch,
	} = useProject();
	const { tierTrial, trialEnded } = useAuth();

	const checkPermission = useTrainingPermission();
	const { start, cancel, isDrawing, layersToShow } = useAnnotationBoundaryDraw();

	// `toolBarVisible` already keeps this and Detect-in-area out of each other's
	// way and covers the single-image case, which map ownership does not. This adds
	// the owners it does not cover — a boundary edit, and measurement mode. Only
	// the start branch is gated; cancelling must stay reachable once the flow owns
	// the map itself.
	const { busy: mapBusy, reason: blockedReason } = useMapToolBusy();

	const annotationButtonTooltipText = () => {
		if (tierTrial) {
			if (trialEnded) return 'Your trial has ended and you can no longer annotate';
			if (pickedTask?.multimodel) return 'You can not train a multimodel as a trial user';
			if (pickedTask?.is_trained) return 'You can not improve accuracy as a trial user';
		}

		if (detections.length === 0) return 'Make manual annotations to train the AI model';

		return 'Enhance AI accuracy by fine-tuning the annotations';
	};

	const prepareTraining = () => {
		if (!checkPermission({ task: pickedTask })) return;

		start({
			fetchGrid: () =>
				getGridFeatures({ project_uuid: project.uuid, model_uuid: pickedTask.model_uuid }),
			onReady: allowTraining,
		});
	};

	const allowTraining = gridFeatures => {
		const taskLabels = pickedTask.classes.map(task => task.description);
		const taskIds = Array.from(Array(taskLabels.length).keys());

		dispatch({
			type: 'setAnnotationMode',
			payload: { gridFeatures, target: ANNOTATION_TARGET.MODEL },
		});

		dispatch({
			type: 'setAnnotationSidebarData',
			payload: {
				classIds: taskIds,
				labels: taskLabels,
				activeClassId: taskIds[0],
			},
		});

		setActiveSidebar({ sidebarId: annotationSidebarId });
	};

	if (!mapObject || !pickedTask || selectedSingleImage) return null;

	if (annotationMode && annotationMode.target !== ANNOTATION_TARGET.OBJECT) {
		if (projectMode === PROJECT_MODE.ORTHOPHOTO) {
			return (
				<AnnotateOrthophoto
					layersToShow={layersToShow}
					hideAnnotationSidebar={() => {
						removeFromActiveSidebars(annotationSidebarId);
					}}
				/>
			);
		} else if (projectMode === PROJECT_MODE.SINGLE_IMAGE) {
			return <AnnotateSingleImages layersToShow={layersToShow} />;
		}
	}

	// Both states share the toolbar's button chrome; only these four values differ.
	const isImproving = detections.length > 0;
	const action = isDrawing
		? {
				onClick: cancel,
				disabled: isDemo,
				icon: <IoCloseCircleOutline className="m-1 mb-2" />,
				label: isImproving ? 'Cancel Accuracy Improvement' : 'Cancel Training',
				tooltip: isImproving
					? 'Cancel the accuracy improvement'
					: 'Cancel training and keep the current model',
			}
		: {
				onClick: prepareTraining,
				disabled: !toolBarVisible || isDemo || mapBusy,
				icon: <IoBuild className="m-1 mb-2" style={{ transform: 'rotate(-90deg)' }} />,
				label: isImproving ? 'Improve AI Accuracy' : 'Train AI',
				tooltip: blockedReason || annotationButtonTooltipText(),
			};

	return (
		<>
			<Tooltip id="annotate" variant="light" />
			<Button
				disabled={action.disabled}
				onClick={action.onClick}
				variant="dark-secondary"
				className="btn-dark secondary"
				data-tooltip-id="tooltip-toolbar-root"
				data-tooltip-content={action.tooltip}
				data-tooltip-place="top">
				{action.icon}
				{action.label}
			</Button>
		</>
	);
}
